import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';

/**
 * ProtectedUserRoute Component
 * 
 * Wraps user-facing routes (leaderboard, etc.) to ensure only
 * authenticated PBFS employees can access them via direct URL.
 * 
 * Security: Checks MSAL login + tenant ID (any @pyebarkerfs.com employee).
 * This is DIFFERENT from ProtectedRoute which checks admin group membership.
 * 
 * Author: Matt Hales
 * Created: February 2026
 * Related: User Story #10262
 */

const PBFS_TENANT_ID = process.env.REACT_APP_AZURE_TENANT_ID || '53a5d716-7fec-428f-97f0-51db8b7a9877';

const ProtectedUserRoute = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidTenant, setIsValidTenant] = useState(false);

  const isNativePlatform = window.Capacitor?.isNativePlatform?.() === true;
  const hasNativeToken = isNativePlatform && !!localStorage.getItem('token');

  useEffect(() => {
    // On native with a stored token, trust the auth — the token was obtained
    // through ASWebAuthenticationSession and the backend validates it on every call.
    if (hasNativeToken && accounts.length === 0) {
      setIsValidTenant(true);
      setIsVerifying(false);
      return;
    }

    const verifyTenant = async () => {
      if (accounts.length === 0) {
        setIsVerifying(false);
        return;
      }

      try {
        const account = accounts[0];
        const idTokenClaims = account.idTokenClaims;
        const tokenTid = idTokenClaims?.tid;

        if (tokenTid === PBFS_TENANT_ID) {
          console.log('✅ PBFS tenant verified for user route');

          const existingToken = localStorage.getItem('token');
          if (!existingToken) {
            try {
              const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: account
              });
              if (tokenResponse?.idToken) {
                localStorage.setItem('token', tokenResponse.idToken);
              }
            } catch (error) {
              if (error.name === 'InteractionRequiredAuthError') {
                try {
                  await instance.acquireTokenRedirect({
                    ...loginRequest,
                    account: account
                  });
                } catch (redirectError) {
                  console.error('❌ Token redirect failed:', redirectError);
                }
              }
            }
          }
          setIsValidTenant(true);
        } else {
          console.log('❌ Wrong tenant:', tokenTid);
          setIsValidTenant(false);
        }
      } catch (error) {
        console.error('Error verifying tenant:', error);
        setIsValidTenant(false);
      } finally {
        setIsVerifying(false);
      }
    };

    if (accounts.length > 0 && inProgress === 'none') {
      verifyTenant();
    } else if (inProgress === 'none') {
      setIsVerifying(false);
    }
  }, [accounts, instance, inProgress, hasNativeToken]);

  // Not logged in — on native, don't redirect (the bottom nav handles auth prompting)
  if (accounts.length === 0 && !hasNativeToken && inProgress === 'none') {
    instance.loginRedirect(loginRequest);
    return (
      <div className="min-h-screen bg-[var(--pbfs-navy)] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[var(--pbfs-green)] mx-auto mb-4"></div>
          <p>Redirecting to Microsoft login...</p>
        </div>
      </div>
    );
  }

  // Login in progress
  if (inProgress !== 'none' || isVerifying) {
    return (
      <div className="min-h-screen bg-[var(--pbfs-navy)] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[var(--pbfs-green)] mx-auto mb-4"></div>
          <p>Verifying access...</p>
        </div>
      </div>
    );
  }

  // Logged in but wrong tenant
  if (!isValidTenant) {
    return (
      <div className="min-h-screen bg-[var(--pbfs-navy)] text-white flex items-center justify-center">
        <div className="max-w-md p-8 bg-[#002a42] rounded-lg border border-white/10">
          <h1 className="text-2xl font-bold mb-4 text-red-400">
            Access Denied
          </h1>
          <p className="text-white/70 mb-4">
            This feature is only available to Pye-Barker Fire & Safety employees.
          </p>
          <p className="text-white/50 text-sm mb-4">
            Logged in as: <strong>{accounts[0]?.username}</strong>
          </p>
          <button
            onClick={() => instance.logout()}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedUserRoute;
